"use client";
import { useCallback, useMemo } from "react";

import {
  HydratedChatbotAsset,
  trpc,
  useChatbotActionPermissions,
  useDuplicateChatbot,
  useMoveAssetsToFolder,
  useSupportedAssets,
} from "@zapier/client";
import { useTranslations } from "@zapier/i18n";
import {
  KebabMenuItem,
  useEmitChatbotKebabInteractionEvent,
} from "@zapier/shared";
import { ChatbotModal } from "./ChatbotModals";
import { HydratedChatbotWithFolder } from "../useChatbotsList";
import { parseFolderId, useNotification } from "@zapier/utils";
import { useParams } from "next/navigation";
import { useAccountActionPermissions } from "@zapier/client";

interface Input {
  chatbot: HydratedChatbotWithFolder | HydratedChatbotAsset;
  setCurrentModal: (modal: ChatbotModal | undefined) => void;
  setCurrentChatbot: (
    chatbot: HydratedChatbotWithFolder | HydratedChatbotAsset | undefined,
  ) => void;
  onCopyToAnotherAccount?: (asset: HydratedChatbotAsset) => void;
}

export function useChatbotRow({
  chatbot,
  setCurrentModal,
  setCurrentChatbot,
  onCopyToAnotherAccount,
}: Input) {
  const { createNotification } = useNotification();
  const { isMPFEnabled } = useSupportedAssets();
  const permissions = useChatbotActionPermissions(chatbot.id ?? "");

  const { isChatbotsEnabled } = useAccountActionPermissions();

  // Get folderId from URL if present (route param), otherwise from table object
  const parameters = useParams();
  const folderIdFromUrl = parameters?.folder
    ? parseFolderId(String(parameters.folder))
    : undefined;
  const folderIdFromTable = (chatbot as HydratedChatbotWithFolder).folder?.id;
  const folderId = folderIdFromUrl || folderIdFromTable;

  const utils = trpc.useUtils();

  const { moveAssetsToFolder } = useMoveAssetsToFolder({
    folderId,
  });
  const { mutate: duplicateChatbot, isPending: isDuplicating } =
    useDuplicateChatbot({
      folderId,
    });
  const { emit } = useEmitChatbotKebabInteractionEvent();
  const t = useTranslations("chatbot-row-menu");
  const tAction = useTranslations("buttons");

  const handleRenameChatbot = useCallback(() => {
    setCurrentChatbot(chatbot);
    setCurrentModal(ChatbotModal.Rename);
    emit("rename", chatbot.id);
  }, [setCurrentChatbot, chatbot, setCurrentModal, emit]);

  const handleChangeOwner = useCallback(() => {
    setCurrentChatbot(chatbot);
    setCurrentModal(ChatbotModal.ChangeOwner);
    emit("change_owner", chatbot.id);
  }, [setCurrentChatbot, chatbot, setCurrentModal, emit]);

  const handleDeleteChatbot = useCallback(() => {
    setCurrentChatbot(chatbot);
    setCurrentModal(ChatbotModal.Delete);
    emit("delete", chatbot.id);
  }, [setCurrentChatbot, chatbot, setCurrentModal, emit]);

  const handleMoveChatbot = useCallback(() => {
    setCurrentChatbot(chatbot);
    setCurrentModal(ChatbotModal.Move);
    emit("move", chatbot.id);
  }, [setCurrentChatbot, chatbot, setCurrentModal, emit]);

  const handleShareChatbot = useCallback(() => {
    setCurrentChatbot(chatbot);
    setCurrentModal(ChatbotModal.Share);
    emit("share", chatbot.id);
  }, [setCurrentChatbot, chatbot, setCurrentModal, emit]);

  const handleDuplicateChatbot = () => {
    setCurrentChatbot(chatbot);
    duplicateChatbot(
      { chatbotId: chatbot.id! },
      {
        onSuccess: (data) => {
          const newChatbotId = data?.id;

          createNotification({
            message: t("notifications.duplicate-success", {
              chatbotName: chatbot.name ?? "",
            }),
            type: "success",
          });
          emit("duplicate", chatbot.id);

          // If the original chatbot is in a folder, move the duplicate to the same folder
          // Do this after showing the notification so it happens asynchronously
          if (folderId && newChatbotId) {
            moveAssetsToFolder(
              {
                assetIds: [`chatbot:${newChatbotId}`],
                destinationFolderId: folderId,
              },
              {
                onSuccess: () => {
                  // Invalidate folder assets and lookups (will refetch when needed)
                  if (folderIdFromUrl) {
                    utils.asset.getHydratedFolderAssets.invalidate({
                      folderId: folderIdFromUrl,
                    });
                  }
                  utils.folder.foldersLookup.invalidate();
                },
                onError: () => {
                  createNotification({
                    message: t("notifications.duplicate-move-error"),
                    type: "error",
                  });
                },
              },
            );
          }
        },
        onError: () => {
          createNotification({
            message: t("notifications.duplicate-error", {
              chatbotName: chatbot.name ?? "",
            }),
            type: "error",
          });
        },
      },
    );
  };

  const changeOwnerTooltipText = useMemo(() => {
    if (permissions.isLoading) {
      return "";
    }
    return permissions.hasTeams
      ? t("tooltips.no-action-permission", { action: "change the owner of" })
      : t("tooltips.change-owner-personal-account");
  }, [permissions.hasTeams, permissions.isLoading, t]);

  const menuItems = useMemo(() => {
    return [
      {
        disabled: !permissions.canRename,
        icon: "actionEdit",
        label: t("actions.rename"),
        onClick: handleRenameChatbot,
      },
      {
        label: "Share chatbot",
        icon: "personAdd",
        onClick: handleShareChatbot,
        disabled: !permissions.hasTeams,
        tooltipText: permissions.hasTeams
          ? undefined
          : t("tooltips.share-personal-account"),
      },
      {
        label: t("actions.duplicate"),
        onClick: handleDuplicateChatbot,
        icon: "actionCopy",
        disabled:
          !permissions.canDuplicate || isDuplicating || !isChatbotsEnabled,
        tooltipText: isChatbotsEnabled
          ? permissions.isLoading
            ? undefined
            : t("tooltips.no-action-permission", { action: "duplicate" })
          : t("actions.duplicate-disabled-tooltip"),
      },
      isMPFEnabled && {
        disabled: !permissions.canMove,
        icon: "actionMoveToFolder",
        label: t("actions.move-to-folder"),
        onClick: handleMoveChatbot,
      },
      {
        label: t("actions.change-owner"),
        onClick: handleChangeOwner,
        icon: "arrowConnect",
        disabled: !permissions.canChangeOwner,
        tooltipText: changeOwnerTooltipText,
      },
      { divider: true, label: "divider" },
      onCopyToAnotherAccount && {
        icon: "arrowExit",
        label: tAction("copy-to-another-account"),
        onClick: () => {
          onCopyToAnotherAccount(chatbot as HydratedChatbotAsset);
        },
      },
      onCopyToAnotherAccount && {
        divider: true,
        label: "divider",
      },
      {
        disabled: !permissions.canDelete,
        hasWarning: true,
        icon: "actionTrash",
        label: t("actions.delete"),
        onClick: handleDeleteChatbot,
        tooltipText: permissions.isLoading
          ? undefined
          : t("tooltips.no-action-permission", { action: "delete" }),
      },
    ].filter(Boolean) as KebabMenuItem[];
  }, [
    permissions,
    changeOwnerTooltipText,
    handleChangeOwner,
    handleDeleteChatbot,
    handleRenameChatbot,
    handleShareChatbot,
    t,
    isMPFEnabled,
    onCopyToAnotherAccount,
    tAction,
    chatbot,
    handleMoveChatbot,
    isChatbotsEnabled,
  ]);

  return {
    menuItems,
    changeOwnerTooltipText,
    triggerPermissionsCheck: permissions.enablePermissionsCheck,
    handleShareChatbot,
  };
}
